/**
 * Stories implemented
 */
/* eslint-disable local-rules/require-storybook-documentation */
import { FunctionComponent } from 'react';
import { ZillowNavbarHeader } from './components/ZillowNavbarHeader';
import { ZillowNavbarContentContainer } from './components/ZillowNavbarContentContainer';
import { SkipNavigationLink } from './components/SkipNavigationLink';
import { ZillowNavbarNav } from './components/ZillowNavbarNav';
import { ZillowNavbarSectionsListGroup, ZILLOW_NAVBAR_GROUP_SIDE } from './components/ZillowNavbarSectionsListGroup';
import { ZillowNavbarSections } from './components/ZillowNavbarSections/ZillowNavbarSections';
import { ZillowNavbarHamburger } from './components/ZillowNavbarHamburger/ZillowNavbarHamburger';
import { useZillowNavbarPresentationalState } from './hooks/useZillowNavbarPresentationalState';
import { ZillowNavbarMobileDropdownMenu } from './components/ZillowNavbarMobileDropdownMenu';
import { HEADER_VERSIONS } from './constants';
import { ZillowNavbarLogo } from './components/ZillowNavbarLogo';
import { HeaderLinksConfiguration } from './page-frame-types/HeaderLinksConfiguration';
import { StaticDomainConfiguration } from './page-frame-types/StaticDomainConfiguration';
import { AccountManagement } from './components/AccountManagement/AccountManagement';
import { UserNavigationData } from './components/AccountManagement/types/UserNavigationData';

type Auth0Options =
    | { useAuth0?: false }
    | {
          useAuth0: true;
          onAuth0SignIn: () => void;
          onAuth0SignOut: () => void;
      };

type ZillowNavbarProps = {
    /**
     * Which header version to render
     */
    version: HEADER_VERSIONS;
    /**
     * User's session data
     */
    userNavigationData: UserNavigationData | null;
    /**
     * Utilize Auth0 rather than Zillow Auth account management experience
     */
    useAuth0?: boolean;
    zhlHost: string;
    /**
     * Page Frame links to render
     */
    links: HeaderLinksConfiguration;
    /**
     * The configuration for fetching the static assets
     */
    staticDomainConfig: StaticDomainConfiguration;
} & Auth0Options;
const SKIP_TO_MAIN_CONTENT_ID = 'skip-topnav-target';

/**
 * A re-implementation of the Page Frame zillow navbar to replace the page-frame navbar
 * while the page-frame navbar does not support Auth0 account integration.
 */
export const ZillowNavbar: FunctionComponent<ZillowNavbarProps> = ({
    userNavigationData,
    staticDomainConfig,
    links,
    useAuth0 = false,
    zhlHost,
    version,
    // @ts-expect-error
    onAuth0SignIn,
    // @ts-expect-error
    onAuth0SignOut,
}) => {
    const { isMobileNavOpen, toggleMobileNav } = useZillowNavbarPresentationalState();
    const { logo, main, agent, marketing, help, regLogin } = links;

    const authModule = (
        <AccountManagement
            userNavigationData={userNavigationData}
            version={version}
            zhlHost={zhlHost}
            useAuth0={useAuth0}
            links={regLogin}
            onAuth0SignIn={onAuth0SignIn}
            onAuth0SignOut={onAuth0SignOut}
        />
    );
    return (
        <>
            <SkipNavigationLink href={`#${SKIP_TO_MAIN_CONTENT_ID}`}>
                <span>Skip main navigation</span>
            </SkipNavigationLink>
            <ZillowNavbarHeader version={version} isMobileNavOpen={isMobileNavOpen}>
                <ZillowNavbarNav>
                    <ZillowNavbarContentContainer>
                        <ZillowNavbarSectionsListGroup
                            isMobileNavOpen={isMobileNavOpen}
                            version={version}
                            isMobile
                            side={ZILLOW_NAVBAR_GROUP_SIDE.LEFT}>
                            <ZillowNavbarHamburger
                                version={version}
                                onClick={toggleMobileNav}
                                isMobileNavOpen={isMobileNavOpen}
                            />
                        </ZillowNavbarSectionsListGroup>
                        <ZillowNavbarSectionsListGroup
                            isMobileNavOpen={isMobileNavOpen}
                            version={version}
                            side={ZILLOW_NAVBAR_GROUP_SIDE.LEFT}>
                            <ZillowNavbarSections version={version} zhlHost={zhlHost} links={main} />
                            <ZillowNavbarSections version={version} zhlHost={zhlHost} links={agent} />
                        </ZillowNavbarSectionsListGroup>
                        <ZillowNavbarLogo
                            logo={logo}
                            isMobileNavOpen={isMobileNavOpen}
                            staticDomain={staticDomainConfig}
                            version={version}
                        />
                        <ZillowNavbarSectionsListGroup
                            isMobileNavOpen={isMobileNavOpen}
                            version={version}
                            side={ZILLOW_NAVBAR_GROUP_SIDE.RIGHT}>
                            <ZillowNavbarSections
                                version={version}
                                zhlHost={zhlHost}
                                links={marketing}
                                useVerticalMegaMenu
                            />
                            <ZillowNavbarSections version={version} zhlHost={zhlHost} links={help} />
                            {authModule}
                        </ZillowNavbarSectionsListGroup>
                        <ZillowNavbarSectionsListGroup
                            isMobileNavOpen={isMobileNavOpen}
                            version={version}
                            isMobile
                            side={ZILLOW_NAVBAR_GROUP_SIDE.RIGHT}>
                            {!isMobileNavOpen && authModule}
                        </ZillowNavbarSectionsListGroup>
                    </ZillowNavbarContentContainer>
                </ZillowNavbarNav>
            </ZillowNavbarHeader>
            <ZillowNavbarMobileDropdownMenu
                version={version}
                zhlHost={zhlHost}
                links={links}
                isOpen={isMobileNavOpen}
            />
            <div id={SKIP_TO_MAIN_CONTENT_ID} tabIndex={-1} />
        </>
    );
};
